import {
  HttpBackend,
  HttpClient,
  HttpContext,
  HttpEvent,
  HttpHeaders,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BYPASS_LOG } from '@core/interceptors/loading-screen.interceptor';
import { environment } from 'environments/environment';
import { ObjectMapper } from 'json-object-mapper';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from 'services/auth.service';
import { DomainService } from 'services/domain.service';
import { HttpService } from 'services/http.service';
import { normalizeDate } from 'utils/helps';
import { PipelineConfigType } from 'services/models/recruitment/enum/PipelineConfigType.enum';
import { RecruitmentCandidateStatus } from 'services/models/recruitment/enum/RecruitmentCandidateStatus.enum';
import { RecruitmentStatus } from 'services/models/recruitment/enum/RecruitmentStatus.enum';
import { RecruitCompanyBenefitsConfigModel } from 'services/models/recruitment/recruit-company-benefits-config.model';
import { RecruitPipelineConfigModel } from 'services/models/recruitment/recruit-pipeline-config.model';
import { RecruitRejectionReasonModel } from 'services/models/recruitment/recruit-rejection-reason.model';
import { RecruitProcessReasonType } from 'services/models/recruitment/enum/RecruitProcessReasonType.enum';
import { RecruitmentCustomFieldInputType } from 'services/models/recruitment/enum/RecruitmentCustomFieldInputType.enum';
import { RecruitmentAdmissionCustomFieldItemModel } from 'services/models/recruitment/recruitment-admission-custom-field-item.model';
import { RecruitmentCustomFieldItemModel } from 'services/models/recruitment/recruitment-custom-field-item.model';
import { RecruitPageConfigModel } from 'services/models/recruitment/recruitment-page-config.model';
import { RecruitmentModel } from 'services/models/recruitment/recruitment.model';
import { Response } from 'services/models/response';
import { OptionModel } from 'services/models/model';
import {
  RecruitmentMetricsModel,
  RecruitmentMetricsParams
} from 'services/models/recruitment/recruitment-metrics.model';
import { RecruitmentSummaryModel } from 'services/models/recruitment/recruitment-summary.model';
import { ServiceBase } from 'services/servicebase';
import { RecruitPageRecruitersConfig } from 'services/models/recruitment/recruit-page-recruiters-config.model';

/** Parâmetros opcionais OData para `GET page/open-positions`. */
export interface OpenPositionsODataParams {
  $filter?: string;
  $orderby?: string;
  $skip?: number;
  $top?: number;
}

@Injectable()
export class RecruitmentService extends ServiceBase<RecruitmentModel> {
  private httpClientNoInterceptors: HttpClient;

  constructor(
    http: HttpService,
    private httpClient: HttpClient,
    handler: HttpBackend,
    private authService: AuthService,
    private domainService: DomainService
  ) {
    super('recruitment', http, environment.apiUrl);
    this.httpClientNoInterceptors = new HttpClient(handler);
  }

  createEntity(input: any): RecruitmentModel {
    return RecruitmentModel.create(input);
  }

  getSummary(filter: string): Observable<RecruitmentSummaryModel> {
    const query = filter ? `?${filter.replace(/^[?&]/, '')}` : '';

    return this.http
      .get(`${this.endpoint}/summary${query}`, undefined, this.baseUrl, null, true)
      .pipe(map(response => RecruitmentSummaryModel.create(response?.body)));
  }

  getMetrics(filters: RecruitmentMetricsParams | null = null): Observable<RecruitmentMetricsModel> {
    return this.http
      .get(`${this.endpoint}/metrics`, { params: this.buildMetricsParams(filters) }, this.baseUrl, null, true)
      .pipe(map(response => RecruitmentMetricsModel.create(response?.body)));
  }

  private buildMetricsParams(filters: RecruitmentMetricsParams | null): HttpParams {
    let params = new HttpParams();

    if (!filters) {
      return params;
    }

    Object.entries(filters).forEach(([key, value]: [string, unknown]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(item => {
          if (item !== null && item !== undefined && item !== '') {
            params = params.append(key, String(item));
          }
        });
        return;
      }

      params = params.set(key, String(value));
    });

    return params;
  }

  getPageConfig(): Observable<RecruitPageConfigModel> {
    return this.http.get(`${this.endpoint}/config/page`, null, this.baseUrl).pipe(
      map((response: any) => {
        const body = response?.body || {};
        const config = RecruitPageConfigModel.create(body);

        if (body.urlContentCDN) {
          config.videoFileUrl = body.urlContentCDN;
        } else if (body.videoFileUrl) {
          config.videoFileUrl = body.videoFileUrl;
        }

        if (body.linkedInUrl) {
          config.linkedinUrl = body.linkedInUrl;
        } else if (body.linkedinUrl) {
          config.linkedinUrl = body.linkedinUrl;
        }

        if (!config.linkedinUrl || config.linkedinUrl.trim() === '') {
          config.linkedinUrl = null;
        }
        if (!config.xUrl || config.xUrl.trim() === '') {
          config.xUrl = null;
        }
        if (!config.facebookUrl || config.facebookUrl.trim() === '') {
          config.facebookUrl = null;
        }
        if (!config.instagramUrl || config.instagramUrl.trim() === '') {
          config.instagramUrl = null;
        }

        return config;
      }),
      catchError((error: any) => {
        console.warn('Erro ao carregar configuração da página, retornando modelo padrão:', error);
        const defaultConfig = new RecruitPageConfigModel();
        defaultConfig.enable = true;
        return of(defaultConfig);
      })
    );
  }

  getPageRecruiters(): Observable<RecruitPageRecruitersConfig> {
    return this.http
      .get(`${this.endpoint}/config/page/recruiters`, null, this.baseUrl, undefined, true)
      .pipe(
        map((response: HttpResponse<string[]>) => ({
          recruiterIds: Array.isArray(response?.body)
            ? response.body.filter((id): id is string => typeof id === 'string' && id.trim() !== '')
            : [],
        })),
        catchError(() =>
          of({
            recruiterIds: [],
          })
        )
      );
  }

  savePageRecruitersConfig(payload: RecruitPageRecruitersConfig): Observable<void> {
    const recruiterIds = (payload?.recruiterIds ?? []).filter(
      (id): id is string => typeof id === 'string' && id.trim() !== ''
    );

    const body = {
      recruiterIds,
    };

    return this.http.post(`${this.endpoint}/config/page/recruiters`, body).pipe(map(() => undefined));
  }

  getCompanyBenefitsConfigured(): Observable<{ hasConfiguredBenefits: boolean }> {
    return this.http
      .get(`${this.endpoint}/config/company-benefits/configured`, null, this.baseUrl, undefined, true)
      .pipe(
        map((response: any) => {
          const body = response?.body ?? {};
          const flag = body.hasConfiguredBenefits ?? body.HasConfiguredBenefits;
          return { hasConfiguredBenefits: flag === true };
        }),
        catchError(() => of({ hasConfiguredBenefits: false }))
      );
  }

  getCompanyBenefitsConfig(): Observable<RecruitCompanyBenefitsConfigModel> {
    return this.http
      .get(`${this.endpoint}/config/company-benefits`, null, this.baseUrl, undefined, true)
      .pipe(
        map((response: HttpResponse<unknown>) => {
          const body = response.body;
          return RecruitCompanyBenefitsConfigModel.create(body);
        }),
        catchError(() => of(RecruitCompanyBenefitsConfigModel.create({ benefits: [] })))
      );
  }

  saveCompanyBenefitsConfig(payload: RecruitCompanyBenefitsConfigModel): Observable<void> {
    const body = {
      companyGroupId: payload.companyGroupId,
      benefits: (payload.benefits ?? []).map(b => ({
        companyRootId: b.companyRootId,
        benefit: b.benefit ?? '',
      })),
    };
    return this.http.put(`${this.endpoint}/config/company-benefits`, body).pipe(map(() => undefined));
  }

  getCompanyBenefitForCompanyRoot(companyRootId: string): Observable<{ benefit: string | null }> {
    return this.http
      .get(
        `${this.endpoint}/config/company-benefits/for-company-root?companyRootId=${encodeURIComponent(companyRootId)}`,
        null,
        this.baseUrl,
        undefined,
        true
      )
      .pipe(
        map((response: any) => {
          const body = response?.body ?? {};
          const benefit = body.benefit ?? body.Benefit ?? null;
          return { benefit: benefit == null ? null : String(benefit) };
        }),
        catchError(() => of({ benefit: null }))
      );
  }

  listCustomFields(
    jobTitleFamilyId: string,
    jobTitleGroupId: string
  ): Observable<RecruitmentCustomFieldItemModel[]> {
    const params = new HttpParams({
      fromObject: {
        jobTitleFamilyId,
        jobTitleGroupId,
      },
    });

    return this.httpClient
      .get<any>(`${this.baseUrl}/${this.endpoint}/custom-fields`, {
        params,
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(
        map((data: any) => {
          const body: any[] = data?.body || data || [];
          return body.map((item: any) => RecruitmentCustomFieldItemModel.create(item));
        })
      );
  }

  listAdmissionCustomFields(): Observable<RecruitmentAdmissionCustomFieldItemModel[]> {
    return this.http
      .get(`${this.endpoint}/admission-custom-fields`, undefined, this.baseUrl, undefined, true)
      .pipe(
        map((data: any) => {
          const body = data?.body ?? data;
          const arr = Array.isArray(body) ? body : [];
          return arr.map((item: any) =>
            RecruitmentAdmissionCustomFieldItemModel.create(this.mapAdmissionFieldDtoRecursive(item))
          );
        })
      );
  }

  saveAdmissionCustomFields(items: RecruitmentAdmissionCustomFieldItemModel[]): Observable<any> {
    const payload = (items || []).map(i => this.mapItemToAdmissionPayload(i));
    return this.httpClient.post<any>(
      `${this.baseUrl}/${this.endpoint}/admission-custom-fields`,
      payload
    );
  }

  removeAdmissionCustomFieldElement(
    queryParams: { itemId: string; optionId: string },
    disableLoading = true
  ): Observable<any> {
    const opt = {
      context: new HttpContext().set(BYPASS_LOG, disableLoading),
      params: new HttpParams({ fromObject: queryParams }),
    };
    return this.httpClient.delete<any>(
      `${this.baseUrl}/${this.endpoint}/admission-custom-fields/remove-element`,
      opt
    );
  }

  private normalizeAdmissionFieldDto(dto: any): any {
    const inputTypeEnum = RecruitmentCustomFieldInputType.getById(Number(dto.inputType));
    return {
      ...dto,
      companyGroupId: dto.companyGroupId ?? dto.CompanyGroupId ?? '',
      inputType: inputTypeEnum?.value ?? String(dto.inputType ?? ''),
      options: (dto.options || dto.Options || []).map((o: any, idx: number) => ({
        id: o.id,
        admissionCustomFieldItemId:
          o.admissionCustomFieldItemId ??
          o.recruitmentAdmissionCustomFieldItemId ??
          o.RecruitmentAdmissionCustomFieldItemId ??
          '',
        label: o.label,
        legend: o.legend ?? '',
        order: o.order ?? idx,
      })),
    };
  }

  mapAdmissionFieldDtoRecursive(dto: any): any {
    if (!dto) {
      return dto;
    }
    const normalized = this.normalizeAdmissionFieldDto(dto);
    const children = dto.itemChildren ?? dto.ItemChildren;
    normalized.itemChildren = Array.isArray(children)
      ? children.map((c: any) => this.mapAdmissionFieldDtoRecursive(c))
      : [];
    return normalized;
  }

  private mapItemToAdmissionPayload(item: RecruitmentAdmissionCustomFieldItemModel): any {
    const inputTypeEnum = RecruitmentCustomFieldInputType.getBy(item.inputType) as
      | RecruitmentCustomFieldInputType
      | undefined;
    return {
      id: item.id,
      label: item.label,
      inputType: inputTypeEnum?.id ?? 5,
      placeholder: item.placeholder ?? '',
      description: item.description ?? '',
      parentItemId: item.parentItemId || null,
      required: !!item.required,
      order: item.order,
      isUpdate: !!item.isUpdate,
      enable: true,
      options: (item.options || []).map(o => ({
        id: o.id,
        label: o.label,
        legend: o.legend ?? '',
        order: o.order,
        isUpdate: !!item.isUpdate,
      })),
    };
  }

  savePageConfig(config: RecruitPageConfigModel): Observable<RecruitPageConfigModel> {
    const payload: any = {
      id: config.id,
      companyGroupId: config.companyGroupId,
      title: config.title || '',
      description: config.description || '',
      coverFileKey: config.coverFileKey,
      coverFileName: config.coverFileName,
      coverFileUrl: config.coverFileUrl,
      enable: config.enable,
      facebookUrl: config.facebookUrl,
      instagramUrl: config.instagramUrl,
      xUrl: config.xUrl,
      linkedinUrl: config.linkedinUrl,
    };

    if (
      config.fileContent &&
      config.fileContent.fileKey &&
      config.fileContent.fileKey.trim() !== '' &&
      config.fileContent.fileName
    ) {
      payload.fileContent = {
        fileKey: config.fileContent.fileKey,
        fileName: config.fileContent.fileName,
      };
    }

    return this.http.put(`${this.endpoint}/config/page`, payload).pipe(
      map((response: any) => {
        if (!response || !response.body) {
          return config;
        }

        const savedConfig = RecruitPageConfigModel.create(response.body);

        if (response.body && response.body.urlContentCDN) {
          savedConfig.videoFileUrl = response.body.urlContentCDN;
        } else if (response.body && response.body.videoFileUrl) {
          savedConfig.videoFileUrl = response.body.videoFileUrl;
        } else if (
          (!savedConfig.videoFileUrl || savedConfig.videoFileUrl.trim() === '') &&
          config.videoFileUrl
        ) {
          savedConfig.videoFileUrl = config.videoFileUrl;
        }

        if (!savedConfig.fileContent && config.fileContent) {
          savedConfig.fileContent = config.fileContent;
        }

        if (response.body && response.body.linkedInUrl) {
          savedConfig.linkedinUrl = response.body.linkedInUrl;
        } else if (
          (!savedConfig.linkedinUrl || savedConfig.linkedinUrl.trim() === '') &&
          config.linkedinUrl
        ) {
          savedConfig.linkedinUrl = config.linkedinUrl;
        }
        if ((!savedConfig.xUrl || savedConfig.xUrl.trim() === '') && config.xUrl) {
          savedConfig.xUrl = config.xUrl;
        }
        if (
          (!savedConfig.facebookUrl || savedConfig.facebookUrl.trim() === '') &&
          config.facebookUrl
        ) {
          savedConfig.facebookUrl = config.facebookUrl;
        }
        if (
          (!savedConfig.instagramUrl || savedConfig.instagramUrl.trim() === '') &&
          config.instagramUrl
        ) {
          savedConfig.instagramUrl = config.instagramUrl;
        }

        return savedConfig;
      })
    );
  }

  setCoverImage(file: File): Observable<any> {
    if (!file || !(file instanceof File)) {
      return new Observable(observer => observer.error(new Error('Arquivo inválido')));
    }

    const formData = new FormData();
    formData.append('file', file);

    const token = this.authService.getToken();

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    });

    return this.httpClientNoInterceptors.post(
      `${environment.apiUrl}/recruitment/config/page/set-cover`,
      formData,
      {
        observe: 'response',
        reportProgress: true,
        headers: headers,
      }
    );
  }

  getPipelineConfigs(
    type: PipelineConfigType | number,
    includeApprovalDefault: boolean = false
  ): Observable<RecruitPipelineConfigModel[]> {
    const typeValue = typeof type === 'number' ? type : type.id;

    const url = `${environment.apiUrl}/${this.endpoint}/config/pipelines`;

    return this.httpClient
      .get<any>(url, {
        params: {
          type: typeValue.toString(),
          includeApprovalDefault: includeApprovalDefault.toString(),
        },
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(
        map((response: any) => {
          const data = response.data || response.body || response;
          if (!data) {
            return [];
          }

          const configs = RecruitPipelineConfigModel.fromArray(data);
          return configs;
        })
      );
  }

  getVacancyPipelineConfigs(): Observable<RecruitPipelineConfigModel[]> {
    return this.getPipelineConfigs(PipelineConfigType.RECRUITMENT);
  }

  getApplicantPipelineConfigs(): Observable<RecruitPipelineConfigModel[]> {
    return this.getPipelineConfigs(PipelineConfigType.CANDIDATE);
  }

  savePipelineConfigs(
    configs: RecruitPipelineConfigModel[],
    type: PipelineConfigType | number
  ): Observable<any> {
    const typeValue = typeof type === 'number' ? type : type.id;
    const url = `${environment.apiUrl}/${this.endpoint}/config/pipelines`;

    const serializedConfigs = configs.map(config => {
      const obj = config.serialize();

      if (obj.type === null || obj.type === undefined || obj.type === 0) {
        delete obj.type;
        obj.type = typeValue;
      }

      if (obj.statusMapping !== null && obj.statusMapping !== undefined) {
        const normalizedStatus = Number(obj.statusMapping);
        if (!isNaN(normalizedStatus)) {
          obj.statusMapping = normalizedStatus;
        } else {
          delete obj.statusMapping;
        }
      }

      if (
        obj.statusMapping === null ||
        obj.statusMapping === undefined ||
        obj.statusMapping === 0
      ) {
        const fallbackStatus =
          typeValue === PipelineConfigType.CANDIDATE.id
            ? RecruitmentCandidateStatus.IN_PROGRESS.id
            : RecruitmentStatus.DRAFT.id;
        obj.statusMapping = fallbackStatus;
      }

      if (obj.requiredSchedule === null || obj.requiredSchedule === undefined) {
        delete obj.requiredSchedule;
        obj.requiredSchedule = false;
      }

      if (obj.order === null || obj.order === undefined) {
        obj.order = 0;
      }

      return obj;
    });

    return this.httpClient
      .post<any>(url, serializedConfigs, {
        params: { type: typeValue.toString() },
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(
        map((response: any) => {
          return response;
        })
      );
  }

  getOpenPositions(
    odata?: OpenPositionsODataParams
  ): Observable<{ data: RecruitmentModel[]; count: number }> {
    const domain = this.domainService.getDomain();
    const companyGroupId = domain?.id;

    if (!companyGroupId) {
      throw new Error('CompanyGroupId não encontrado');
    }

    let params = new HttpParams().set('companyGroupId', companyGroupId);

    if (odata?.$filter) {
      params = params.set('$filter', odata.$filter);
    }
    if (odata?.$orderby) {
      params = params.set('$orderby', odata.$orderby);
    }
    if (odata?.$skip != null) {
      params = params.set('$skip', String(odata.$skip));
    }
    if (odata?.$top != null) {
      params = params.set('$top', String(odata.$top));
    }

    const url = `${environment.apiUrl}/${this.endpoint}/page/open-positions`;

    return this.httpClient
      .get<any>(url, {
        params,
        observe: 'response',
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(
        map((response: HttpResponse<any>) => {
          const body = response.body;
          let raw: any[] = [];

          if (Array.isArray(body)) {
            raw = body;
          } else if (body?.value && Array.isArray(body.value)) {
            raw = body.value;
          } else if (body?.data && Array.isArray(body.data)) {
            raw = body.data;
          }

          const countHeader =
            response.headers.get('X-Count') ?? response.headers.get('x-count');
          const count =
            countHeader != null && countHeader !== ''
              ? parseInt(countHeader, 10)
              : raw.length;

          return {
            data: raw.map((item: any) => RecruitmentModel.create(item)),
            count,
          };
        })
      );
  }

  getOpenPositionCompanies(): Observable<OptionModel[]> {
    const domain = this.domainService.getDomain();
    const companyGroupId = domain?.id;

    if (!companyGroupId) {
      return of([]);
    }

    const url = `${environment.apiUrl}/${this.endpoint}/page/open-positions/companies`;
    const params = new HttpParams().set('companyGroupId', companyGroupId);

    return this.httpClient
      .get<any>(url, {
        params,
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(
        map((body: any) => {
          const raw = body ?? [];

          return raw.map(
            (item: { id: string; name: string }) =>
              ({
                label: item.name,
                value: item.id,
              }) as OptionModel
          );
        }),
        catchError(() => of([]))
      );
  }

  getRejectionReasons(): Observable<RecruitRejectionReasonModel[]> {
    return this.getProcessReasonsByType(RecruitProcessReasonType.REJECTION.id);
  }

  getWithdrawalReasons(): Observable<RecruitRejectionReasonModel[]> {
    return this.getProcessReasonsByType(RecruitProcessReasonType.CANDIDATE_WITHDRAWAL.id);
  }

  private getProcessReasonsByType(type: number): Observable<RecruitRejectionReasonModel[]> {
    const params = new HttpParams()
      .set('includeInactive', 'false')
      .set('type', String(type))
      .set('$orderby', 'title asc');

    return this.httpClient
      .get<any>(`${environment.apiUrl}/${this.endpoint}/config/rejection-reasons`, {
        params,
        context: new HttpContext().set(BYPASS_LOG, true),
      })
      .pipe(
        map((response: any) => {
          const data = response?.data || response?.value || response?.body || response || [];
          return data.map((item: any) => RecruitRejectionReasonModel.create(item));
        }),
        catchError(error => {
          throw error;
        })
      );
  }

  uploadToS3(url: string, file: File, contentType?: string): Observable<any> {
    const resolvedContentType = (contentType || file?.type || 'application/octet-stream').trim();

    const headers = new HttpHeaders({
      'Content-Type': resolvedContentType,
    });

    return this.httpClientNoInterceptors.put(url, file, {
      headers: headers,
      reportProgress: true,
      observe: 'events',
    });
  }

  getPublicRecruitments(): Observable<RecruitmentModel[]> {
    return this.httpClientNoInterceptors
      .get<any>(`${environment.apiUrl}/recruitment/odata?$filter=isActive eq true`)
      .pipe(
        map((response: any) => {
          if (response && Array.isArray(response)) {
            return response.map((item: any) => RecruitmentModel.create(item));
          }
          return [];
        })
      );
  }

  getPublicRecruitmentById(id: string): Observable<RecruitmentModel> {
    const domain = this.domainService.getDomain();
    const companyGroupId = domain?.id;

    if (!companyGroupId) {
      throw new Error('CompanyGroupId não encontrado');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/page/${id}?companyGroupId=${companyGroupId}`;
    const options = {
      context: new HttpContext().set(BYPASS_LOG, true),
    };

    return this.httpClient.get<any>(url, options).pipe(
      map((response: any) => {
        const data = response.data || response.body || response;
        return RecruitmentModel.create(data);
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  getPreSignedUrlForVideo(
    fileName: string,
    contentType: string
  ): Observable<{ url: string; fileKey: string }> {
    const query = `fileName=${encodeURIComponent(fileName)}&contentType=${encodeURIComponent(contentType)}`;

    return this.http
      .get(
        `${this.endpoint}/config/page/pre-signed-url-video?${query}`,
        null,
        this.baseUrl,
        null,
        true
      )
      .pipe(
        map((res: any) => {
          return res.body;
        })
      );
  }

  getPreSignedByKey(fileKey: string, contentType: string): Observable<string | null> {
    const normalizedFileKey = (fileKey || '').trim();
    const normalizedContentType = (contentType || 'application/octet-stream').trim();

    if (!normalizedFileKey) {
      return of(null);
    }

    const params = new HttpParams()
      .set('filekey', normalizedFileKey)
      .set('contentType', normalizedContentType);

    return this.httpClientNoInterceptors
      .get<any>(`${environment.apiUrl}/${this.endpoint}/config/page/pre-signed-by-key`, {
        params,
      })
      .pipe(
        map((response: any) => {
          const payload = response?.body || response;

          if (typeof payload === 'string' && payload.trim()) {
            return payload;
          }

          if (typeof payload?.url === 'string' && payload.url.trim()) {
            return payload.url;
          }

          if (typeof response?.url === 'string' && response.url.trim()) {
            return response.url;
          }

          return null;
        }),
        catchError(() => of(null))
      );
  }

  uploadVideoToPreSignedUrlWithProgress(
    file: File
  ): Observable<{ event: HttpEvent<any>; url: string; fileKey: string }> {
    return this.getPreSignedUrlForVideo(file.name, file.type).pipe(
      switchMap((data: { url: string; fileKey: string }) => {
        return this.httpClientNoInterceptors
          .put(data.url, file, {
            headers: {
              'Content-Type': file.type,
            },
            withCredentials: false,
            observe: 'events',
            reportProgress: true,
          })
          .pipe(
            map((event: HttpEvent<any>) => ({
              event,
              url: data.url,
              fileKey: data.fileKey,
            }))
          );
      })
    );
  }

  deletePipelineConfig(id: string): Observable<any> {
    if (!id) {
      throw new Error('Nenhum ID encontrado');
    }

    const url = `${environment.apiUrl}/${this.endpoint}/config/pipelines/${id}`;
    return this.httpClient.delete<any>(url).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  public removeCustomFieldElement(
    queryParams: { itemId: string; optionId: string },
    disableLoanding = true
  ): Observable<any> {
    const opt = {
      context: new HttpContext().set(BYPASS_LOG, disableLoanding),
      params: new HttpParams({ fromObject: queryParams }),
    };

    return this.httpClient
      .delete<any>(`${this.baseUrl}/${this.endpoint}/remove-element`, opt)
      .pipe(map(response => response));
  }

  toggleApplications(id: string) {
    return this.httpClient
      .put<any>(`${this.baseUrl}/${this.endpoint}/${id}/toggle-applications`, {})
      .pipe(map(response => response));
  }

  pauseRecruitment(id: string, payload: { reasonId: string; justification?: string }): Observable<void> {
    if (!id) {
      throw new Error('RecruitmentId é obrigatório');
    }

    return this.httpClient
      .put<void>(`${this.baseUrl}/${this.endpoint}/${id}/pause`, {
        reasonId: payload.reasonId,
        justification: payload.justification || '',
      })
      .pipe(map(() => undefined));
  }

  reopenRecruitment(id: string, payload: { reasonId: string; justification?: string }): Observable<void> {
    if (!id) {
      throw new Error('RecruitmentId é obrigatório');
    }

    return this.httpClient
      .put<void>(`${this.baseUrl}/${this.endpoint}/${id}/reopen`, {
        reasonId: payload.reasonId,
        justification: payload.justification || '',
      })
      .pipe(map(() => undefined));
  }

  updateRecruitmentDates(
    id: string,
    publicationStartDate?: string | Date,
    publicationEndDate?: string | Date
  ) {
    if (!id) {
      throw new Error('RecruitmentId é obrigatório');
    }

    const payload = {
      recruitmentId: id,
      publicationStartDate: normalizeDate(publicationStartDate),
      publicationEndDate: normalizeDate(publicationEndDate),
    };

    return this.httpClient
      .put<any>(`${this.baseUrl}/${this.endpoint}/${id}/dates`, payload)
      .pipe(map(response => response));
  }

  publishRecruitment(id: string): Observable<any> {
    if (!id) {
      throw new Error('RecruitmentId é obrigatório');
    }

    return this.httpClient
      .put<any>(`${this.baseUrl}/${this.endpoint}/${id}/publish`, {})
      .pipe(map(response => response));
  }

  updateRecruitment(recruitment: RecruitmentModel): Observable<any> {
    if (!recruitment || !recruitment.id) {
      throw new Error('Recruitment com ID é obrigatório');
    }

    this.http.convertTimes(recruitment);
    const payload = this.omitServerManagedRecruitmentDates(JSON.parse(recruitment.toJSON()));

    return this.httpClient
      .put<any>(`${this.baseUrl}/${this.endpoint}`, payload)
      .pipe(map(response => response));
  }

  assignRecruiter(recruitmentId: string, recruiterId: string): Observable<any> {
    if (!recruitmentId || !recruiterId) {
      throw new Error('RecruitmentId e recruiterId são obrigatórios');
    }

    return this.httpClient
      .put<any>(`${this.baseUrl}/${this.endpoint}/${recruitmentId}/assign-recruiter`, {
        recruiterId,
      })
      .pipe(map(response => response));
  }

  createRecruitment(recruitment: RecruitmentModel): Observable<string | null> {
    if (!recruitment) {
      throw new Error('Recruitment é obrigatório');
    }

    this.http.convertTimes(recruitment);
    let _object: any = ObjectMapper.serialize(recruitment);
    _object = _object.length > 1 ? _object : recruitment.toJSON();
    if (typeof _object === 'string') {
      _object = JSON.parse(_object);
    }
    _object = this.omitServerManagedRecruitmentDates(_object);

    return this.http.post(`/${this.endpoint}`, _object, undefined, this.baseUrl).pipe(
      switchMap(() => {
        return this.findLatestRecruitmentIdByTitleAndCompany(recruitment.title, recruitment.companyId);
      }),
      catchError(() => of(null))
    );
  }

  private findLatestRecruitmentIdByTitleAndCompany(
    title: string,
    companyId: string
  ): Observable<string | null> {
    if (!title?.trim() || !companyId?.trim()) {
      return of(null);
    }
    const esc = (s: string) => s.replace(/'/g, "''");
    const filter = `title eq '${esc(title.trim())}' and companyId eq ${companyId}`;
    const url = `${environment.apiUrl}/${this.endpoint}/odata?$filter=${encodeURIComponent(
      filter
    )}&$orderby=creationDate desc&$top=1&$select=id`;

    return this.httpClient.get<any>(url, { observe: 'response' }).pipe(
      map(r => {
        const body = r.body;
        const row = Array.isArray(body) ? body[0] : null;
        return row?.id ? String(row.id) : null;
      }),
      catchError(() => of(null))
    );
  }

  private omitServerManagedRecruitmentDates(payload: any): any {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    delete payload.finalizationDate;
    return payload;
  }

  createReferralLink(recruitmentId: string): Observable<string> {
    if (!recruitmentId) {
      return of('');
    }

    return this.http
      .post(`/${this.endpoint}/${recruitmentId}/referral-links`, {}, undefined, this.baseUrl, undefined, true)
      .pipe(
        map(res => {
          const code = res?.body?.code || res?.body?.Code || res?.code;
          return (code ?? '').toString();
        })
      );
  }

  getReferralLinkAvailability(recruitmentId: string, code: string): Observable<boolean> {
    if (!recruitmentId || !code) {
      return of(false);
    }

    return this.httpClientNoInterceptors
      .get<{ available?: boolean; Available?: boolean }>(
        `${this.baseUrl}/${this.endpoint}/page/${recruitmentId}/referral-links/${encodeURIComponent(code)}`
      )
      .pipe(map(body => !!(body?.available ?? body?.Available)));
  }

  clone(id: string): Observable<Response<RecruitmentModel>> {
    return this.http
      .get(`${this.endpoint}/${id}/clone`, undefined, this.baseUrl, undefined, true)
      .pipe(
        map(res => {
          const response = new Response<RecruitmentModel>();
          response.data = this.createEntity(res.body || {}) as RecruitmentModel;
          if (response.data) {
            response.data.finalizationDate = null;
          }
          return response;
        })
      );
  }

}
